import React, { useEffect } from 'react';
import { addUnit, resolveImageFilter } from '../utils/styleHelpers';
import { loadCustomFonts, ensureSettingsFontsLoaded } from '../utils/fontLoader';

export const GlobalStyles = ({ settings = {}, config = {}, customFonts = [] }) => {
    const fontsToLoad = customFonts?.length ? customFonts : (config?.customFonts || settings?.customFonts || []);

    useEffect(() => {
        if (fontsToLoad && fontsToLoad.length > 0) {
            loadCustomFonts(fontsToLoad);
        }
        ensureSettingsFontsLoaded(settings, config);
    }, [fontsToLoad, settings, config]);
    const resolveBtnBg = () => {
        if (settings.primaryBgMode === 'transparent' || settings.primaryColor === 'transparent') {
            return 'transparent';
        }
        if (settings.primaryBgMode === 'glass') {
            return 'rgba(255, 255, 255, 0.12)';
        }
        if (settings.primaryBgMode === 'gradient') {
            const start = settings.primaryGradientStart || settings.primaryColor || '#ff0000';
            const mid = settings.primaryGradientMid || null;
            const end = settings.primaryGradientEnd || '#cc0000';
            const angle = settings.primaryGradientAngle || '135';
            const stops = mid ? `${start}, ${mid}, ${end}` : `${start}, ${end}`;
            return `linear-gradient(${angle}deg, ${stops})`;
        }
        return settings.primaryColor || '#ff0000';
    };

    const resolveBtnShadow = () => {
        const preset = settings.buttonShadowPreset;
        if (preset === 'subtle') return '0 2px 8px rgba(0, 0, 0, 0.12)';
        if (preset === 'medium') return '0 4px 16px rgba(0, 0, 0, 0.22)';
        if (preset === 'heavy') return '0 10px 30px rgba(0, 0, 0, 0.38)';
        if (preset === 'glow-primary') return `0 0 20px ${settings.primaryColor && settings.primaryColor !== 'transparent' ? settings.primaryColor : '#3b82f6'}`;
        if (preset === 'glow-colored') return `0 0 20px ${settings.buttonShadowColor || '#3b82f6'}`;
        if (preset === 'inset') return `inset 0 2px 6px ${settings.buttonShadowColor || 'rgba(0,0,0,0.3)'}`;

        const btnShadowColor = settings.buttonShadowColor;
        const btnShadowBlur = addUnit(settings.buttonShadowBlur || '0px');
        const btnShadowX = addUnit(settings.buttonShadowX || '0px');
        const btnShadowY = addUnit(settings.buttonShadowY || '0px');
        const btnShadowSpread = addUnit(settings.buttonShadowSpread || '0px');
        const btnShadowInset = settings.buttonShadowInset ? 'inset ' : '';

        if (btnShadowColor && btnShadowColor !== 'transparent' && (btnShadowBlur !== '0px' || btnShadowX !== '0px' || btnShadowY !== '0px')) {
            return `${btnShadowInset}${btnShadowX} ${btnShadowY} ${btnShadowBlur} ${btnShadowSpread} ${btnShadowColor}`;
        }
        return 'none';
    };

    const btnTextColor = settings.buttonTextColor || '#ffffff';
    const btnBorderWidth = addUnit(settings.buttonBorderWidth || '0px');
    const btnBorderStyle = settings.buttonBorderStyle || 'solid';
    const btnBorderColor = settings.buttonBorderColor || 'transparent';
    const btnBorder = (btnBorderWidth === '0px' || btnBorderStyle === 'none' || btnBorderColor === 'transparent')
        ? 'none'
        : `${btnBorderWidth} ${btnBorderStyle} ${btnBorderColor}`;

    const btnShadow = resolveBtnShadow();
    const isGlass = settings.primaryBgMode === 'glass';

    const imgFilter = resolveImageFilter(settings);
    const hasImageTint = Boolean(settings.imageTint && settings.imageTint !== 'transparent');
    const imageTintOpacity = settings.imageTintOpacity !== undefined ? parseFloat(settings.imageTintOpacity) : 0.4;
    const imageTintBlend = settings.imageTintBlendMode || 'multiply';
    const imgDuration = settings.imageTransitionDuration || '0.4s';
    const imgTiming = settings.imageTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';
    const imgHover = settings.imageHoverFilter || 'none';
    const imgClick = settings.imageClickInteraction || 'none';

    return (
        <style>{`
            *, *::before, *::after { box-sizing: border-box; }
            :root {
                --primary-color: ${settings.primaryColor || '#3b82f6'};
                --global-link-color: ${settings.globalLinkColor || settings.linkColor || settings.primaryColor || '#3b82f6'};
                --global-link-hover-color: ${settings.globalLinkHoverColor || settings.linkHoverColor || '#1d4ed8'};
                --quote-border-color: ${settings.quoteBorderColor || settings.primaryColor || '#3b82f6'};
                --quote-bg-color: ${settings.quoteBgColor || 'transparent'};
                --quote-text-color: ${settings.quoteTextColor || 'inherit'};
                --quote-font-size: ${settings.quoteFontSize ? addUnit(settings.quoteFontSize) : 'inherit'};
                --bullet-color: ${settings.bulletColor || 'inherit'};
                --ol-color: ${settings.olColor || 'inherit'};
                --list-item-color: ${settings.listItemColor || 'inherit'};
                --para-color: ${settings.paragraphColor || 'inherit'};
            }
            input, select, textarea { max-width: 100%; min-width: 0; }
            img { max-width: 100%; height: auto; }
            html, body { overflow-x: hidden; }
            td, th { white-space: nowrap; }

            /* --- GLOBAL IMAGE FILTERS, DESATURATION, HOVER & CLICK INTERACTIONS --- */
            img.styled-img, .content-image, .section-image, article img, .page-content img, .prose img, .shop-product-card-img, .shop-product-image {
                ${imgFilter !== 'none' ? `filter: ${imgFilter} !important; -webkit-filter: ${imgFilter} !important;` : ''}
                transition: filter ${imgDuration} ${imgTiming}, -webkit-filter ${imgDuration} ${imgTiming}, transform ${imgDuration} ${imgTiming}, opacity ${imgDuration} ${imgTiming} !important;
                will-change: filter, transform;
            }

            ${imgHover !== 'none' ? `
            img.styled-img:hover, .content-image:hover, .section-image:hover, article img:hover, .page-content img:hover, .prose img:hover, .shop-product-card-img:hover, .shop-product-image:hover,
            .shop-product-card:hover .shop-product-card-img {
                ${imgHover === 'color-on-hover' ? 'filter: none !important; -webkit-filter: none !important;' :
                  (imgHover === 'zoom-color-on-hover' || imgHover === 'zoom-color') ? 'filter: none !important; -webkit-filter: none !important; transform: scale(1.05) !important;' :
                  imgHover === 'desaturate-on-hover' ? 'filter: grayscale(100%) !important; -webkit-filter: grayscale(100%) !important;' :
                  `filter: ${imgHover} !important; -webkit-filter: ${imgHover} !important;`}
            }
            ` : ''}

            /* Active / Click micro-interaction */
            ${imgClick !== 'none' ? `
            img.styled-img:active, .content-image:active, .section-image:active, article img:active, .page-content img:active, .shop-product-card-img:active,
            .shop-product-card:active .shop-product-card-img {
                ${imgClick === 'active-press' ? 'transform: scale(0.97) !important;' :
                  (imgClick === 'color-on-click' || imgClick === 'toggle') ? 'filter: none !important; -webkit-filter: none !important; transform: scale(0.98) !important;' : ''}
            }
            ` : ''}

            /* Click-toggled class (interactive tap/click state) */
            img.styled-img.is-color-toggled, .content-image.is-color-toggled, .section-image.is-color-toggled, article img.is-color-toggled, .shop-product-card-img.is-color-toggled,
            .is-color-toggled img, [data-color-toggled="true"] img {
                filter: none !important;
                -webkit-filter: none !important;
            }

            ${hasImageTint ? `
            .styled-img-wrap, .img-tint-wrap {
                position: relative;
            }
            .styled-img-wrap::after, .img-tint-wrap::after {
                content: '';
                position: absolute;
                inset: 0;
                background-color: ${settings.imageTint};
                opacity: ${imageTintOpacity};
                mix-blend-mode: ${imageTintBlend};
                pointer-events: none;
                border-radius: inherit;
                transition: opacity ${imgDuration} ${imgTiming}, background-color ${imgDuration} ${imgTiming} !important;
            }
            ${(imgHover === 'color-on-hover' || imgHover === 'zoom-color-on-hover' || imgHover === 'zoom-color') ? `
            .styled-img-wrap:hover::after, .img-tint-wrap:hover::after, .shop-product-card:hover .shop-card-image-tint-overlay {
                opacity: 0 !important;
            }
            ` : imgHover === 'boost-tint-on-hover' ? `
            .styled-img-wrap:hover::after, .img-tint-wrap:hover::after, .shop-product-card:hover .shop-card-image-tint-overlay {
                opacity: ${Math.min(1, imageTintOpacity * 1.5 + 0.2)} !important;
            }
            ` : ''}
            ${(imgClick === 'color-on-click' || imgClick === 'toggle') ? `
            .styled-img-wrap:active::after, .img-tint-wrap:active::after, .shop-product-card:active .shop-card-image-tint-overlay {
                opacity: 0 !important;
            }
            ` : ''}
            .is-color-toggled.styled-img-wrap::after, .is-color-toggled.img-tint-wrap::after, [data-color-toggled="true"] .shop-card-image-tint-overlay {
                opacity: 0 !important;
            }
            ` : ''}

            /* --- LISTS & PARAGRAPH STYLES --- */
            ${settings.paragraphColor ? `p { color: var(--para-color) !important; }` : ''}
            ${settings.bulletColor ? `ul li::marker { color: var(--bullet-color) !important; }` : ''}
            ${settings.olColor ? `ol li::marker { color: var(--ol-color) !important; }` : ''}
            ${settings.listItemColor ? `ul li, ol li { color: var(--list-item-color) !important; }` : ''}
            ${settings.listIndent ? `ul, ol { padding-left: ${addUnit(settings.listIndent)} !important; }` : ''}
            ${settings.listItemSpacing ? `ul li, ol li { margin-bottom: ${addUnit(settings.listItemSpacing)} !important; }` : ''}
            ${settings.paragraphMarginBottom ? `p:not(.hero-subtitle):not(.hero-title):not([data-editable="title"]):not([data-editable="text"]):not([data-no-margin]) { margin-bottom: ${addUnit(settings.paragraphMarginBottom)} !important; }` : ''}

            /* --- CONTAINER LAYOUT --- */
            .container {
                max-width: ${(() => {
                    const w = String(settings.containerMaxWidth || '1100px').trim().toLowerCase();
                    if (['100%', 'full', 'none', 'max'].includes(w)) return '100%';
                    return addUnit(settings.containerMaxWidth || '1100px');
                })()} !important;
                padding-left: ${addUnit(settings.containerPaddingLeftRight !== undefined && settings.containerPaddingLeftRight !== '' ? settings.containerPaddingLeftRight : '20px')} !important;
                padding-right: ${addUnit(settings.containerPaddingLeftRight !== undefined && settings.containerPaddingLeftRight !== '' ? settings.containerPaddingLeftRight : '20px')} !important;
            }
            .container.full-width, .container.is-full-width, .container-fluid, [data-full-width="true"].container {
                max-width: 100% !important;
                width: 100% !important;
            }
            .container.no-padding, [data-edge-to-edge="true"].container {
                padding-left: 0 !important;
                padding-right: 0 !important;
            }
            [data-container-max-w].container {
                max-width: var(--section-container-max-w) !important;
                width: 100% !important;
            }
            [data-container-pad-x].container {
                padding-left: var(--section-container-pad-x) !important;
                padding-right: var(--section-container-pad-x) !important;
            }
            #footer .container {
                max-width: ${settings.footerMaxWidth ? addUnit(settings.footerMaxWidth) : addUnit(settings.containerMaxWidth || '1100px')} !important;
                padding-left: ${settings.footerPaddingLeftRight ? addUnit(settings.footerPaddingLeftRight) : addUnit(settings.containerPaddingLeftRight || '20px')} !important;
                padding-right: ${settings.footerPaddingLeftRight ? addUnit(settings.footerPaddingLeftRight) : addUnit(settings.containerPaddingLeftRight || '20px')} !important;
            }
            .site-navigation .container, .site-navigation .site-nav-container {
                width: ${settings.headerContainerWidth ? addUnit(settings.headerContainerWidth) : '100%'} !important;
                max-width: ${(() => {
                    if (settings.headerWidthMode === 'full') return '100%';
                    if (settings.headerWidthMode === 'wide') return '1400px';
                    if (settings.headerWidthMode === 'compact') return '960px';
                    if (settings.headerMaxWidth) {
                        const val = String(settings.headerMaxWidth).trim().toLowerCase();
                        if (['100%', 'none', 'full'].includes(val)) return '100%';
                        return addUnit(settings.headerMaxWidth);
                    }
                    return addUnit(settings.containerMaxWidth || '1100px');
                })()} !important;
                padding-top: ${addUnit(settings.headerPaddingTop || settings.headerContainerPaddingTop || '0px')} !important;
                padding-bottom: ${addUnit(settings.headerPaddingBottom || settings.headerContainerPaddingBottom || '0px')} !important;
                padding-left: ${addUnit(settings.headerPaddingLeft || settings.headerPaddingLeftRight || settings.containerPaddingLeftRight || '20px')} !important;
                padding-right: ${addUnit(settings.headerPaddingRight || settings.headerPaddingLeftRight || settings.containerPaddingLeftRight || '20px')} !important;
            }

            /* --- DYNAMIC GLOBAL LINKS & HEADER NAVIGATION --- */
            a { color: var(--global-link-color, ${settings.globalLinkColor || settings.linkColor || '#3b82f6'}); }
            a:hover { color: var(--global-link-hover-color, ${settings.globalLinkHoverColor || settings.linkHoverColor || '#1d4ed8'}); }
            nav .nav-desktop a:not(.brand-link), .site-nav .nav-desktop a:not(.brand-link), nav .nav-desktop .nav-item-container > span, .site-nav .nav-desktop .nav-item-container > span {
                color: ${settings.navLinkColor || '#ffffff'} !important;
                font-size: ${addUnit(settings.navFontSize || '0.95rem')} !important;
                font-weight: ${settings.navFontWeight || 'normal'} !important;
                letter-spacing: ${addUnit(settings.navLetterSpacing || 'normal')} !important;
                text-transform: ${settings.navTextTransform || 'none'} !important;
                font-family: ${settings.navFontFamily || 'inherit'} !important;
            }
            nav a:not(.brand-link):hover, .site-nav a:not(.brand-link):hover, nav .nav-item-container > span:hover, .site-nav .nav-item-container > span:hover {
                color: ${settings.navLinkHoverColor || '#cbd5e1'} !important;
            }
            /* Brand link & title text — uses custom brand settings */
            nav .brand-link, .site-nav .brand-link, nav .brand-title-text, .site-nav .brand-title-text {
                color: ${settings.brandColor || '#ffffff'} !important;
                font-size: ${addUnit(settings.brandFontSize || '1.2rem')} !important;
                font-weight: ${settings.brandFontWeight || 'bold'} !important;
                font-family: ${settings.brandFontFamily || 'inherit'} !important;
                font-style: ${settings.brandFontStyle || 'normal'} !important;
                letter-spacing: ${addUnit(settings.brandLetterSpacing || 'normal')} !important;
                text-transform: ${settings.brandTextTransform || 'none'} !important;
                text-decoration: ${settings.brandTextDecoration || 'none'} !important;
                line-height: ${settings.brandLineHeight || 'normal'} !important;
                ${settings.brandTextShadow ? `text-shadow: ${settings.brandTextShadow} !important;` : ''}
                ${settings.brandGradientEnabled ? `
                background-image: ${settings.brandGradient || `linear-gradient(${settings.brandGradientAngle || '135deg'}, ${settings.brandGradientStart || '#3b82f6'}, ${settings.brandGradientEnd || '#ec4899'})`} !important;
                -webkit-background-clip: text !important;
                background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                color: transparent !important;
                ` : ''}
            }
            nav .brand-link:hover, .site-nav .brand-link:hover, nav .brand-title-text:hover, .site-nav .brand-title-text:hover {
                ${settings.brandHoverColor && !settings.brandGradientEnabled ? `color: ${settings.brandHoverColor} !important;` : ''}
            }

            /* --- GRADIENT TEXT INHERITED STYLING --- */
            [style*="background-clip: text"], [style*="-webkit-background-clip: text"] {
                max-width: 100%;
            }
            [style*="background-clip: text"] *, [style*="-webkit-background-clip: text"] * {
                color: inherit !important;
                -webkit-text-fill-color: inherit !important;
                background: transparent !important;
            }

            /* --- GLOBAL BUTTON BASE STYLING --- */
            .btn, a.btn, button.btn, .hero-btn {
                display: ${settings.buttonDisplay || 'inline-block'};
                background: ${resolveBtnBg()};
                color: ${btnTextColor} !important;
                border-radius: ${addUnit(settings.buttonRadius || '4px')};
                border: ${btnBorder};
                box-shadow: ${btnShadow};
                font-weight: ${settings.buttonFontWeight || 'bold'};
                font-size: ${addUnit(settings.buttonFontSize || '1rem')};
                ${settings.buttonFontFamily ? `font-family: ${settings.buttonFontFamily};` : ''}
                text-transform: ${settings.buttonTextTransform || 'none'};
                letter-spacing: ${addUnit(settings.buttonLetterSpacing || 'normal')};
                padding: ${settings.buttonPadding || '12px 24px'};
                ${settings.buttonMargin ? `margin: ${settings.buttonMargin};` : ''}
                ${settings.buttonWidth && settings.buttonWidth !== 'auto' ? `width: ${settings.buttonWidth};` : ''}
                text-decoration: none;
                transition: ${settings.buttonTransition || 'all 0.25s ease'};
                cursor: pointer;
                ${isGlass ? 'backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);' : ''}
            }

            /* --- BUTTON VARIANT OVERRIDES --- */
            .btn-primary, a.btn-primary, button.btn-primary {
                background: ${resolveBtnBg()} !important;
                color: ${btnTextColor} !important;
            }
            .btn-secondary, a.btn-secondary, button.btn-secondary {
                background: ${settings.secondaryColor === 'transparent' ? 'transparent' : (settings.secondaryColor || '#10b981')} !important;
                color: ${btnTextColor} !important;
            }
            .btn-tertiary, a.btn-tertiary, button.btn-tertiary {
                background: ${settings.tertiaryColor === 'transparent' ? 'transparent' : (settings.tertiaryColor || '#8b5cf6')} !important;
                color: ${btnTextColor} !important;
            }
            .btn-white, a.btn-white, button.btn-white {
                background: #ffffff !important;
                color: #333333 !important;
            }
            .btn-outline, a.btn-outline, button.btn-outline, .btn-transparent, a.btn-transparent, button.btn-transparent {
                background: transparent !important;
                border: ${btnBorderWidth !== '0px' ? btnBorder : `2px solid ${settings.primaryColor && settings.primaryColor !== 'transparent' ? settings.primaryColor : '#3b82f6'}`} !important;
                color: ${settings.buttonTextColor && settings.buttonTextColor !== '#ffffff' ? settings.buttonTextColor : (settings.primaryColor && settings.primaryColor !== 'transparent' ? settings.primaryColor : '#3b82f6')} !important;
            }

            /* --- BUTTON HOVER & ANIMATION RULES --- */
            .btn:hover, a.btn:hover, button.btn:hover, .hero-btn:hover {
                ${settings.buttonHoverBg ? `background: ${settings.buttonHoverBg} !important;` : ''}
                ${settings.buttonHoverTextColor ? `color: ${settings.buttonHoverTextColor} !important;` : ''}
                ${settings.buttonHoverBorderColor ? `border-color: ${settings.buttonHoverBorderColor} !important;` : ''}
                ${settings.buttonHoverEffect === 'scale' ? 'transform: scale(1.05) !important;' : ''}
                ${settings.buttonHoverEffect === 'lift' ? 'transform: translateY(-3px) !important;' : ''}
                ${settings.buttonHoverEffect === 'glow' ? `box-shadow: 0 0 20px ${settings.buttonHoverShadowColor || settings.primaryColor || '#3b82f6'} !important;` : ''}
                ${settings.buttonHoverEffect === 'fade' ? 'opacity: 0.85 !important;' : ''}
            }

            /* --- BRAND DESKTOP VS MOBILE VISIBILITY HELPERS --- */
            .brand-logo-mobile, .brand-title-mobile, .brand-text-mobile-only {
                display: none !important;
            }

            /* --- RESPONSIVE MOBILE STYLES --- */
            @media (max-width: 991px) {
                ${settings.mobileHeaderPaddingTopBottom ? `.site-navigation { padding-top: ${addUnit(settings.mobileHeaderPaddingTopBottom)} !important; padding-bottom: ${addUnit(settings.mobileHeaderPaddingTopBottom)} !important; }` : ''}
                .site-navigation .container, .site-navigation .site-nav-container {
                    padding-top: ${addUnit(settings.mobileHeaderPaddingTop || '0px')} !important;
                    padding-bottom: ${addUnit(settings.mobileHeaderPaddingBottom || '0px')} !important;
                    padding-left: ${addUnit(settings.mobileHeaderPaddingLeft || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingLeft || settings.headerPaddingLeftRight || '16px')} !important;
                    padding-right: ${addUnit(settings.mobileHeaderPaddingRight || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingRight || settings.headerPaddingLeftRight || '16px')} !important;
                }

                /* --- MOBILE LOGO & BRAND CONTAINER OVERRIDES --- */
                ${(settings.mobileLogoPosition === 'center' || settings.mobileLogoCenter) ? `
                .site-navigation .site-nav-container .brand-link,
                .site-navigation .site-nav-container .brand-editable-wrap {
                    margin-left: auto !important;
                    margin-right: auto !important;
                    justify-content: center !important;
                }
                ` : settings.mobileLogoPosition === 'right' ? `
                .site-navigation .site-nav-container .brand-link,
                .site-navigation .site-nav-container .brand-editable-wrap {
                    margin-left: auto !important;
                    justify-content: flex-end !important;
                }
                ` : ''}
                ${settings.mobileBrandStackDirection ? `
                .site-navigation .brand-content-wrap {
                    flex-direction: ${settings.mobileBrandStackDirection} !important;
                    align-items: ${settings.mobileBrandStackDirection === 'column' ? (settings.mobileLogoPosition === 'center' || settings.mobileLogoCenter ? 'center' : (settings.mobileLogoPosition === 'right' ? 'flex-end' : 'flex-start')) : 'center'} !important;
                }
                ` : ''}
                ${settings.mobileLogoTextGap ? `
                .site-navigation .brand-content-wrap {
                    gap: ${addUnit(settings.mobileLogoTextGap)} !important;
                }
                ` : ''}
                ${settings.mobileSiteLogo ? `
                .site-navigation .brand-logo-desktop { display: none !important; }
                .site-navigation .brand-logo-mobile { display: block !important; }
                ` : ''}
                ${settings.mobileHideLogo ? `
                .site-navigation .brand-logo-img,
                .site-navigation .brand-link img,
                .site-navigation .brand-logo-edit-wrap {
                    display: none !important;
                }
                ` : ''}
                .site-navigation .brand-logo-img, .site-navigation .brand-link img {
                    ${(settings.mobileLogoMaxHeight || settings.mobileLogoHeight) ? `max-height: ${addUnit(settings.mobileLogoMaxHeight || settings.mobileLogoHeight)} !important;` : ''}
                    ${(settings.mobileLogoMaxWidth || settings.mobileLogoWidth) ? `max-width: ${addUnit(settings.mobileLogoMaxWidth || settings.mobileLogoWidth)} !important;` : ''}
                    ${settings.mobileLogoObjectFit ? `object-fit: ${settings.mobileLogoObjectFit} !important;` : ''}
                    ${settings.mobileLogoBorderRadius ? `border-radius: ${addUnit(settings.mobileLogoBorderRadius)} !important;` : ''}
                    ${settings.mobileLogoMargin ? `margin: ${addUnit(settings.mobileLogoMargin)} !important;` : ''}
                    ${settings.mobileLogoPadding ? `padding: ${addUnit(settings.mobileLogoPadding)} !important;` : ''}
                }

                /* --- MOBILE HEADER TITLE & SUBTITLE OVERRIDES --- */
                ${settings.mobileShowTitle === 'hide' ? `
                .site-navigation .brand-text-group,
                .site-navigation .brand-title-text {
                    display: none !important;
                }
                ` : settings.mobileShowTitle === 'show' ? `
                .site-navigation .brand-text-mobile-only {
                    display: flex !important;
                }
                ` : ''}
                ${settings.mobileSiteTitle ? `
                .site-navigation .brand-title-desktop { display: none !important; }
                .site-navigation .brand-title-mobile { display: inline-block !important; }
                ` : ''}
                nav .brand-link, .site-nav .brand-link, nav .brand-title-text, .site-nav .brand-title-text, .site-navigation .brand-title-text {
                    ${settings.mobileBrandFontFamily ? `font-family: ${settings.mobileBrandFontFamily} !important;` : ''}
                    ${settings.mobileBrandFontSize ? `font-size: ${addUnit(settings.mobileBrandFontSize)} !important;` : ''}
                    ${settings.mobileBrandFontWeight ? `font-weight: ${settings.mobileBrandFontWeight} !important;` : ''}
                    ${settings.mobileBrandColor && !settings.brandGradientEnabled ? `color: ${settings.mobileBrandColor} !important; -webkit-text-fill-color: ${settings.mobileBrandColor} !important;` : ''}
                    ${settings.mobileBrandLetterSpacing ? `letter-spacing: ${addUnit(settings.mobileBrandLetterSpacing)} !important;` : ''}
                    ${settings.mobileBrandLineHeight ? `line-height: ${settings.mobileBrandLineHeight} !important;` : ''}
                    ${settings.mobileBrandTextTransform && settings.mobileBrandTextTransform !== 'inherit' ? `text-transform: ${settings.mobileBrandTextTransform} !important;` : ''}
                    ${settings.mobileBrandFontStyle && settings.mobileBrandFontStyle !== 'inherit' ? `font-style: ${settings.mobileBrandFontStyle} !important;` : ''}
                    ${settings.mobileBrandTextShadow && settings.mobileBrandTextShadow !== 'inherit' ? `text-shadow: ${settings.mobileBrandTextShadow} !important;` : ''}
                    ${settings.mobileBrandTextAlign ? `text-align: ${settings.mobileBrandTextAlign} !important;` : ''}
                    ${settings.mobileBrandWhiteSpace === 'normal' ? `white-space: normal !important;` : settings.mobileBrandWhiteSpace === 'ellipsis' ? `white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important;` : settings.mobileBrandWhiteSpace === 'nowrap' ? `white-space: nowrap !important;` : ''}
                    ${settings.mobileBrandMaxWidth ? `max-width: ${addUnit(settings.mobileBrandMaxWidth)} !important;` : ''}
                    ${settings.mobileBrandMargin ? `margin: ${addUnit(settings.mobileBrandMargin)} !important;` : ''}
                    ${settings.mobileBrandPadding ? `padding: ${addUnit(settings.mobileBrandPadding)} !important;` : ''}
                }
                ${settings.mobileHideHeaderSubtitle ? `
                .site-navigation .brand-subtitle-text {
                    display: none !important;
                }
                ` : ''}
                .site-navigation .brand-subtitle-text {
                    ${settings.mobileHeaderSubtitleFontSize ? `font-size: ${addUnit(settings.mobileHeaderSubtitleFontSize)} !important;` : ''}
                    ${settings.mobileHeaderSubtitleColor ? `color: ${settings.mobileHeaderSubtitleColor} !important;` : ''}
                }
                ${settings.mobileHeaderSubtitlePlacement && settings.mobileHeaderSubtitlePlacement !== 'inherit' ? `
                .site-navigation .brand-text-group {
                    flex-direction: ${settings.mobileHeaderSubtitlePlacement === 'beside' ? 'row' : 'column'} !important;
                    align-items: ${settings.mobileHeaderSubtitlePlacement === 'beside' ? 'center' : (settings.mobileBrandTextAlign === 'center' ? 'center' : (settings.mobileBrandTextAlign === 'right' ? 'flex-end' : 'flex-start'))} !important;
                }
                ` : ''}

                .responsive-grid {
                    flex-direction: ${settings.mobileStackOrder === 'reverse' ? 'column-reverse' : 'column'} !important;
                    gap: ${addUnit(settings.mobileColGap || '20px')} !important;
                }
                .container {
                    padding-left: ${addUnit(settings.mobileContainerPadding || '15px')} !important;
                    padding-right: ${addUnit(settings.mobileContainerPadding || '15px')} !important;
                }
                ${(settings.mobileTitleSize || settings.mobileHeadingSize) ? `.hero-title, .hero-title *, .section-title, .section-title *, [data-editable="title"], [data-editable="title"] * { font-size: ${addUnit(settings.mobileTitleSize || settings.mobileHeadingSize)} !important; }` : ''}
                ${(settings.mobileTitleAlign || settings.mobileHeadingAlign) ? `.hero-title, .section-title, [data-editable="title"] { text-align: ${settings.mobileTitleAlign || settings.mobileHeadingAlign} !important; }` : ''}
                ${settings.mobileH1Size ? `h1, h1 *, .h1, .h1 *, [data-editable="h1"], [data-editable="h1"] * { font-size: ${addUnit(settings.mobileH1Size)} !important; }` : ''}
                ${settings.mobileH1Align ? `h1, .h1, [data-editable="h1"] { text-align: ${settings.mobileH1Align} !important; }` : ''}
                ${settings.mobileH2Size ? `h2, h2 *, .h2, .h2 *, [data-editable="h2"], [data-editable="h2"] *, [data-editable="heading"], [data-editable="heading"] * { font-size: ${addUnit(settings.mobileH2Size)} !important; }` : ''}
                ${settings.mobileH2Align ? `h2, .h2, [data-editable="h2"], [data-editable="heading"] { text-align: ${settings.mobileH2Align} !important; }` : ''}
                ${settings.mobileH3Size ? `h3, h3 *, .h3, .h3 *, [data-editable="h3"], [data-editable="h3"] *, [data-editable="text"], [data-editable="text"] * { font-size: ${addUnit(settings.mobileH3Size)} !important; }` : ''}
                ${settings.mobileH3Align ? `h3, .h3, [data-editable="h3"], [data-editable="text"] { text-align: ${settings.mobileH3Align} !important; }` : ''}
                ${settings.mobileBodySize ? `p, p *, li, li *, .body-text, .body-text *, [data-editable="body"], [data-editable="body"] *, [data-editable="richtext"], [data-editable="richtext"] * { font-size: ${addUnit(settings.mobileBodySize)} !important; }` : ''}
                ${(settings.mobileBodyAlign || settings.mobileTextAlign) ? `p, li, .body-text, [data-editable="body"], [data-editable="richtext"] { text-align: ${settings.mobileBodyAlign || settings.mobileTextAlign} !important; }` : ''}
            }

            /* --- CUSTOM USER BUTTON CSS --- */
            ${settings.buttonCustomCss || ''}
        `}</style>
    );
};

export default GlobalStyles;
